import { connect } from 'react-redux';
import { DonationsActions, ProjectsActions } from '../../../redux/actions';
import { useEffect, useMemo } from 'react';
import { getUtmParams } from '../v2/components/utils/utm';
import styled from '@emotion/styled';
import { defaultImage, Logo } from 'images';
import { numberWithCommas } from '../../../utils';
import ProgressBar from '../v2/components/helpers/ProgressBar';
import config from '../../../config';

const resolveWidgetImage = (project) => {
  const imageCandidates = [
    ...(Array.isArray(project?.media?.images) ? project.media.images : []),
    ...(Array.isArray(project?.images) ? project.images : []),
    ...(Array.isArray(project?.mobile_array_images)
      ? project.mobile_array_images
      : []),
    project?.banner,
    project?.image,
    project?.cover_image,
    project?.image_url,
    project?.project_image,
  ].filter(Boolean);

  return imageCandidates[0] || defaultImage;
};

function EmbedShareWidget(props) {
  const { project, projectSupporters, showProject, match, location } = props;

  const projectUrl = match?.params?.url;
  const activeProjectUrl = project?.url || project?.basicInfo?.url;

  const utmParams = useMemo(
    () => getUtmParams(location?.search || ''),
    [location?.search]
  );

  useEffect(() => {
    if (projectUrl && activeProjectUrl !== projectUrl) {
      showProject(projectUrl);
    }
  }, [projectUrl, activeProjectUrl, showProject]);

  const projectLink = useMemo(() => {
    if (!projectUrl) {
      return null;
    }

    const params = new URLSearchParams();

    Object.entries(utmParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });

    const query = params.toString();

    return query
      ? `${config.REACT_APP_URL}project/${projectUrl}?${query}`
      : `${config.REACT_APP_URL}project/${projectUrl}`;
  }, [projectUrl, utmParams]);

  const currentAmount = Number(project?.donations_sum || 0);
  const goalAmount = Number(project?.final_goal || project?.goal || 0);
  const supporterCount = Number(
    projectSupporters ?? project?.donations_count ?? 0
  );

  const progressPercent =
    goalAmount > 0 ? Math.min((currentAmount / goalAmount) * 100, 100) : 0;

  const imageUrl = resolveWidgetImage(project);
  const title = project?.title || project?.basicInfo?.title || '';

  const isTicketable = Boolean(project?.is_ticketable);
  const isFinished =
    project?.status === 'successful' || project?.status === 'finished';

  const buttonLabel = isFinished
    ? isTicketable
      ? 'Ticketverkauf beendet'
      : 'Projekt beendet'
    : isTicketable
      ? 'Ticket buchen'
      : 'Jetzt unterstützen';

  return (
    <WidgetWrapper>
      <CoverImage imageUrl={imageUrl} />

      <Content>
        <LogoImage src={Logo} alt="commonsplace logo" />

        <Title>{title}</Title>

        <ProgressSection>
          <ProgressBar percentage={progressPercent} />
          {!isTicketable && (
            <ProgressInfo>
              <AmountText>
                {numberWithCommas(currentAmount.toFixed(2))} € von{' '}
                {numberWithCommas(goalAmount.toFixed(0))} €
              </AmountText>

              <SupporterText>
                {numberWithCommas(supporterCount || 0)} Unterstützer
              </SupporterText>
            </ProgressInfo>
          )}

          {isTicketable && (
            <ProgressInfo>
              <SupporterText>
                {numberWithCommas(supporterCount || 0)} Besucher
              </SupporterText>
            </ProgressInfo>
          )}
        </ProgressSection>

        {projectLink ? (
          <ButtonRow>
            {/*<ProjectButton href={projectLink} target="_blank">*/}
            {/*  Zum Projekt*/}
            {/*</ProjectButton>*/}
            <DonateButton href={projectLink} target="_blank">
              {buttonLabel}
            </DonateButton>
          </ButtonRow>
        ) : (
          <ButtonRow>
            <ProjectButtonDisabled>Zum Projekt</ProjectButtonDisabled>
            <DonateButtonDisabled>{buttonLabel}</DonateButtonDisabled>
          </ButtonRow>
        )}
      </Content>
    </WidgetWrapper>
  );
}

const mapDispatchToProps = (dispatch) => ({
  showProject: (url) => dispatch(ProjectsActions.showProject(url)),
  updateDraft: (id, project) =>
    dispatch(ProjectsActions.updateDraft(id, project)),
  getProjectDonations: (projectId, perPage, page) =>
    dispatch(DonationsActions.getProjectDonations(projectId, perPage, page)),
});

const mapStateToProps = (state) => {
  const { updates, project, loading, errors, randomProjects } = state.projects;
  const {
    loading: loadingDonations,
    errors: errorsDonations,
    donations,
  } = state.donations;

  return {
    project,
    updates,
    loading,
    errors,
    donations,
    loadingDonations,
    errorsDonations,
    randomProjects,
  };
};

const WidgetWrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  aspect-ratio: 5 / 2;
  height: min(40vw, 200px);
  max-height: 200px;
  background: #ffffff;
  border: 1px solid #e9eaeb;
  box-shadow: 0 4px 12px rgba(10, 13, 18, 0.08);
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 360px) {
    aspect-ratio: auto;
  }
`;

const CoverImage = styled.div`
  width: 40%;
  height: 100%;
  max-height: 200px;
  flex: 0 0 40%;
  background-image: ${({ imageUrl }) => `url(${imageUrl})`};
  background-size: cover;
  background-position: center;
  background-color: #e9eaeb;

  @media (max-width: 360px) {
    flex-basis: 34%;
  }
`;

const Content = styled.div`
  min-width: 0;
  flex: 1;
  width: 60%;
  height: 100%;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  gap: 10px;
  box-sizing: border-box;

  @media (max-width: 420px) {
    padding: 10px;
    gap: 8px;
  }

  @media (max-width: 360px) {
    padding: 8px;
    gap: 6px;
  }
`;

const LogoImage = styled.img`
  width: 90px;
  height: 20px;
  flex: 0 0 auto;
  object-fit: contain;
`;

const Title = styled.div`
  width: 100%;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  min-height: 40px;
  color: #181d27;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (max-width: 420px) {
    font-size: 13px;
    line-height: 18px;
    min-height: 36px;
  }

  @media (max-width: 360px) {
    font-size: 12px;
    line-height: 16px;
    min-height: 32px;
  }
`;

const ProgressSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;

  .progress-bar {
    height: 6px;
    border-radius: 9999px;
  }

  .progress-bar div {
    height: 6px;
    background: #9dbc60;
    border-radius: 9999px;
  }
`;

const ProgressInfo = styled.div`
  width: 100%;
  height: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;

  @media (max-width: 360px) {
    gap: 4px;
  }
`;

const AmountText = styled.div`
  min-width: 0;
  font-family: Inter, sans-serif;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  color: #3f621a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 360px) {
    font-size: 11px;
  }
`;

const SupporterText = styled.div`
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  text-align: right;
  color: #717680;
  white-space: nowrap;

  @media (max-width: 420px) {
    display: none;
  }
`;

const buttonBaseStyles = `
  box-sizing: border-box;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 12px;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.05),
    inset 0 0 0 1px rgba(10, 13, 18, 0.18),
    inset 0 -2px 0 rgba(10, 13, 18, 0.05);
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
`;

const ButtonRow = styled.div`
  width: 100%;
  height: 36px;
  display: grid;
  //grid-template-columns: minmax(84px, 0.72fr) minmax(0, 1fr);
  align-items: center;
  gap: 8px;

  @media (max-width: 360px) {
    height: auto;
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

const projectButtonStyles = `
  ${buttonBaseStyles}
  width: 100%;
  background: #ffffff;
  border-color: #d5d7da;
  color: #414651 !important;
`;

const donateButtonStyles = `
  ${buttonBaseStyles}
  width: 100%;
  background: #9dbc60;
  color: #ffffff !important;
`;

const ProjectButton = styled.a`
  ${projectButtonStyles}

  &:hover {
    color: #414651 !important;
  }
`;

const ProjectButtonDisabled = styled.div`
  ${projectButtonStyles}
  cursor: not-allowed;
  opacity: 0.75;
`;

const DonateButton = styled.a`
  ${donateButtonStyles}
  ${props => (props.$disabled ? `
    cursor: not-allowed;
    opacity: 0.75;
    pointer-events: none;
  ` : '')}

  &:hover {
    color: #ffffff !important;
  }
`;

const DonateButtonDisabled = styled.div`
  ${donateButtonStyles}
  opacity: 0.75;
  pointer-events: none;
`;

export default connect(mapStateToProps, mapDispatchToProps)(EmbedShareWidget);
